// src/features/merchantConfig/components/ObjectSection.jsx
//
// Collapsible header + body for an object node in the configuration tree.
// Default expansion state is depth-aware per UI prompt §5:
//   depth <= 1: expanded by default
//   depth >= 2: collapsed by default

import React, { useState, useId } from "react";
import { ChevronDown, ChevronRight, Inbox } from "lucide-react";
import { humanizeLabel } from "../lib/humanizeLabel";

/**
 * @param {{
 *   fieldKey?: string,
 *   labelOverride?: string,
 *   depth: number,
 *   childCount: number,
 *   children: React.ReactNode,
 *   isEmpty?: boolean,
 *   defaultOpen?: boolean,
 * }} props
 */
export default function ObjectSection({
  fieldKey,
  labelOverride,
  depth,
  childCount,
  children,
  isEmpty,
  defaultOpen,
}) {
  const computedDefault = defaultOpen ?? depth <= 1;
  const [open, setOpen] = useState(computedDefault);
  const panelId = useId();
  const headingId = useId();
  const label = labelOverride ?? (fieldKey ? humanizeLabel(fieldKey) : "");

  if (isEmpty) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Inbox className="h-4 w-4" aria-hidden="true" />
          <span className="font-medium text-gray-700">{label || "Section"}</span>
          <span className="text-gray-400">- no data</span>
        </div>
      </div>
    );
  }

  // Top-level (depth 0) sections render without a heading button - the
  // page header / detail-panel header already provides the title.
  if (depth === 0) {
    return (
      <section className="space-y-1.5" aria-labelledby={headingId}>
        <h3
          id={headingId}
          className="sr-only"
        >
          {label || "Configuration"}
        </h3>
        <div className="space-y-0.5">{children}</div>
      </section>
    );
  }

  const indent = Math.min(depth - 1, 3); // cap indent so deeply-nested objects don't disappear off-screen

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-lg border border-gray-200 bg-white"
      style={{ marginLeft: `${indent * 8}px` }}
    >
      <button
        type="button"
        id={headingId}
        aria-controls={panelId}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-t-lg px-4 py-2.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 text-gray-500" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" aria-hidden="true" />
          )}
          <span className="text-sm font-semibold text-gray-900">{label}</span>
        </span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          {childCount} {childCount === 1 ? "field" : "fields"}
        </span>
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headingId}
          className="border-t border-gray-100 px-2 py-2"
        >
          {children}
        </div>
      )}
    </section>
  );
}
